import * as _ from 'underscore';

import { getAppStore } from '~src/app/AppStore';
import { SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';

import { DataConnectionTypes } from '../../content/resources/DataConnectionTypes';
import { Favorites } from '../../content/resources/Favorites';
import { ProjectNames } from '../../content/resources/ProjectNames';
import { Tags } from '../../content/resources/Tags';
import { UserNames } from '../../content/resources/UserNames';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import {
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { describeLegacyAdminSchedule } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { Filter } from '../../ts/helpers/Filter';
import { Order } from '../../ts/helpers/Order';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { ScheduleContentType } from '../../ts/models/ContentTypes';
import { ISiteUserInfo } from '../../ts/models/ISiteUserInfo';
import { ISliceable } from '../../ts/models/ISliceable';
import { ITypeaheadSource } from '../../ts/models/ITypeaheadSource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { SchedulesService } from '../../ts/resources/Schedules';
import { IDataConnectionType } from '../../ts/ServerApi/types/IDataConnection';
import { IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { FilterFieldNames } from '../../ts/ServerApi/types/IFilterClause';
import { GroupKeys, IGroup } from '../../ts/ServerApi/types/IGroup';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { IProjectName } from '../../ts/ServerApi/types/IProject';
import { ISiteNameSansId } from '../../ts/ServerApi/types/ISiteNameSansId';
import { ITag } from '../../ts/ServerApi/types/ITag';
import { IUserName, UserKeys } from '../../ts/ServerApi/types/IUser';
import { NamedItemKeys } from '../../ts/ServerApi/types/resourceParts';
import { SiteNameSansIds } from '../../ts/siteManagement/services/resources/SiteNameSansIds';
import { Groups } from '../../userManagement/groups/Groups';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import { Users } from '../../userManagement/services/resources/Users';

export namespace SearchSuggestionsService {

  export function project(id: string): Promise<IFieldValue> {
    if (id === 'PersonalSpace') {
      return Promise.resolve({
                     value: 'PersonalSpace',
                     label: Messages.personalSpaceTitle(),
                     count: 0
                     });
    }
    return ProjectNames.getById(id).then((projectName: IProjectName) => {
      return {
        value: projectName ? projectName.id : id,
        label: projectName ? projectName.name : '',
        count: 0
      };
    });
  }

  export function sites(matchText: string): ISliceable<ISiteNameSansId> {
    return SiteNameSansIds.instance.all().withFilters([Filter.search(matchText)]);
  }

  export function favorites(matchText: string): ISliceable<IFavorite> {
    return Favorites.all().withFilters([Filter.search(matchText)]);
  }

  export function users(matchText: string): ResourceQuery<IUserName> {
    return UserNames.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(UserKeys.displayName)]);
  }

  export function usersWithoutGuest(matchText: string): ResourceQuery<IUserName> {
    return users(matchText).addFilter(Users.instance.getWithoutGuestFilter());
  }

  export function usersCanOwnProject(matchText: string): ResourceQuery<IUserName> {
    return users(matchText).addFilter(Users.instance.getCanOwnProjectFilter());
  }

  export function currentLoggedInUser(matchText: string): ResourceQuery<IUserName> {
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    return users(matchText).addFilter(Users.instance.getByUserIdsFilter([userId]));
  }

  export function user(id: string): Promise<IFieldValue> {
    return UserNames.getById(id).then((userName: IUserName) => {
      return {
        value: userName ? userName.id : id,
        label: userName ? userName.displayName : '',
        count: 0
      };
    });
  }

  export function groups(matchText: string): ISliceable<IGroup> {
    return Groups.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(GroupKeys.name)]);
  }

  export function tags(matchText: string): ISliceable<ITag> {
    return Tags.all()
      .withFiltersAndOrder([Filter.search(matchText)], [Order.asc(NamedItemKeys.name)]);
  }

  export function dataConnectionType(dataConnectionType: string): Promise<IFieldValue> {
    return DataConnectionTypes.getByDataConnectionType(dataConnectionType).then((connectionType: IDataConnectionType) => {
      return {
        value: connectionType ? connectionType.connectionType : dataConnectionType,
        label: connectionType ? connectionType.connectionTypeDisplayName : '',
        count: 0
      };
    });
  }

  export function schedulesSource(scheduledActionType?: string): ITypeaheadSource {
    const searchFilterFn = (schedule: IScheduleInfo, search: string) => {
      if(!search) return true;
      const scheduleName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName() ? describeLegacyAdminSchedule(schedule) : schedule.name;
      return _.all(search.toLowerCase().split(' '), (searchPart) => (scheduleName.toLowerCase().indexOf(searchPart) >= 0));
    };

    let scheduleResourceQuery = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(SchedulesService.filterByActionType(scheduledActionType));
    }

    const scheduleListPromise = SliceableHelper.fetchAll(scheduleResourceQuery).then((schedulesList) => schedulesList.items);

    return SliceableHelper.searchArray(searchFilterFn, scheduleListPromise);
  }

  export function schedulesQuery(scheduledActionType?: string): ResourceQuery<IScheduleInfo> {
    let scheduleResourceQuery = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
    if(scheduledActionType) {
      scheduleResourceQuery = scheduleResourceQuery.addFilter(SchedulesService.filterByActionType(scheduledActionType));
    }
    return scheduleResourceQuery;
  }

  // gets users with siteRole filter and search string
  export function usersBySiteRoleAndSearch(siteRole: string, searchText: string, resultOrder: IOrderClause<string>): ResourceQuery<ISiteUserInfo> {
    if (siteRole === SiteRolesResource.AnyRole) {
      return SiteUsers.instance.all().withOrder([resultOrder])
      .addFilter(Filter.search(searchText));
    }
    return SiteUsers.instance.all().withOrder([resultOrder])
    .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
    .addFilter(Filter.search(searchText));
  }
}

export type SearchSuggestionsService = typeof SearchSuggestionsService;
